import React, { useState, useCallback, useMemo, useEffect } from 'react'
import '../index.css'
import debounce from 'lodash/debounce'
import theRifterArr from '../data/theRifterArr'
import RifterCard from './RifterCard'
import RifterThumb from './RifterThumb'
import SkeletonCard from './SkeletonCard'
import Fuse from 'fuse.js'

// Enhanced search configurations for different modes
const searchConfigs = {
  all: {
    shouldSort: true,
    threshold: 0.3,
    location: 0,
    distance: 1000,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: [
      { name: 'article', weight: 0.3 },
      { name: 'author', weight: 0.05 },
      { name: 'artist', weight: 0.05 },
      { name: 'tags', weight: 0.1 },
      { name: 'games', weight: 0.1 },
      { name: 'issueTitle', weight: 0.2 },
      { name: 'summary', weight: 0.2 }
    ]
  },
  articles: {
    shouldSort: true,
    threshold: 0.2,
    location: 0,
    distance: 500,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: [
      { name: 'article', weight: 0.7 },
      { name: 'summary', weight: 0.3 }
    ]
  },
  authors: {
    shouldSort: true,
    threshold: 0.2,
    location: 0,
    distance: 500,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: [
      { name: 'author', weight: 0.8 },
      { name: 'artist', weight: 0.2 }
    ]
  },
  issues: {
    shouldSort: true,
    threshold: 0.2,
    location: 0,
    distance: 500,
    maxPatternLength: 32,
    minMatchCharLength: 1,
    keys: [
      { name: 'issueTitle', weight: 1.0 }
    ]
  }
}

const RifterList = () => {
  const [searchResults, setSearchResults] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [searchMode, setSearchMode] = useState('all')
  const [sortBy, setSortBy] = useState('relevance')
  const [showOfficialOnly, setShowOfficialOnly] = useState(false)
  const [selectedGames, setSelectedGames] = useState(['All'])

  // Memoize the deduplicated array to avoid recalculating on every render
  const deDupArr = useMemo(() => 
    theRifterArr.filter(
      (rifter, index, self) =>
        self.findIndex(r => r.issueTitle === rifter.issueTitle) === index
    ), []
  )

  // Get unique game systems for filter
  const availableGames = useMemo(() => {
    const games = new Set()
    theRifterArr.forEach(rifter => {
      rifter.games.forEach(game => {
        if (game && game !== 'Any' && game !== 'All') games.add(game)
      })
    })
    return Array.from(games).sort()
  }, [])

  // Enhanced search with filters and sorting
  const performEnhancedSearch = useCallback((term) => {
    setIsLoading(true)
    
    setTimeout(() => {
      let results = []
      
      if (term.trim()) {
        // Check if this looks like an issue search (contains "Rifter" and "#")
        const isIssueSearch = term.toLowerCase().includes('rifter') && term.includes('#')
        
        if (isIssueSearch) {
          // For issue searches, filter to ONLY that specific issue
          results = theRifterArr.filter(rifter => 
            rifter.issueTitle === term
          )
        } else {
          // Use Fuse.js with the selected search mode for regular searches
          const config = searchConfigs[searchMode]
          const fuse = new Fuse(theRifterArr, config)
          results = fuse.search(term).map(result => result.item)
        }
      } else {
        results = []
      }

      // Apply filters
      if (showOfficialOnly) {
        results = results.filter(rifter => rifter.official === 'Yes')
      }

      if (selectedGames.length > 0 && !selectedGames.includes('All')) {
        results = results.filter(rifter => 
          rifter.games.some(game => selectedGames.includes(game))
        )
      }

      // Apply sorting
      if (sortBy === 'author') {
        results.sort((a, b) => {
          const authorA = a.author.join(', ').toLowerCase()
          const authorB = b.author.join(', ').toLowerCase()
          return authorA.localeCompare(authorB)
        })
      } else if (sortBy === 'issue') {
        results.sort((a, b) => {
          const issueA = a.issueTitle.toLowerCase()
          const issueB = b.issueTitle.toLowerCase()
          return issueA.localeCompare(issueB)
        })
      }
      // 'relevance' sorting is handled by Fuse.js

      setSearchResults(results)
      setIsLoading(false)
    }, 100)
  }, [searchMode, showOfficialOnly, selectedGames, sortBy])

  // Highlight search terms in text
  const highlightText = useCallback((text, searchTerm) => {
    if (!searchTerm.trim()) return text
    
    // Don't highlight if this looks like an issue search and we're highlighting the issue title
    const isIssueSearch = searchTerm.toLowerCase().includes('rifter') && searchTerm.includes('#')
    if (isIssueSearch && text === searchTerm) {
      return text // Don't highlight the issue title when searching for that specific issue
    }
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
  }, [])

  // Create debounced search function
  const debouncedSearch = useMemo(
    () => debounce(performEnhancedSearch, 300),
    [performEnhancedSearch]
  )

  const handleChange = useCallback((event) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }, [debouncedSearch])

  const listArticles = useCallback((issue) => {
    setSearchTerm(issue)
    // Trigger immediate search for the issue
    performEnhancedSearch(issue)
  }, [performEnhancedSearch])

  // Handle search mode change
  const handleSearchModeChange = useCallback((mode) => {
    setSearchMode(mode)
  }, [])

  // Handle sorting change
  const handleSortChange = useCallback((sort) => {
    setSortBy(sort)
  }, [])

  // Handle official filter toggle
  const handleOfficialFilter = useCallback((checked) => {
    setShowOfficialOnly(checked)
  }, [])

  // Handle game filter change
  const handleGameFilter = useCallback((game, checked) => {
    setSelectedGames(prev => {
      if (game === 'All') {
        // If "All" is checked, clear all other selections
        return checked ? ['All'] : []
      } else {
        // If a specific game is checked, remove "All" if it exists
        let newSelection = checked 
          ? [...prev.filter(g => g !== 'All'), game]
          : prev.filter(g => g !== game)
        
        // If no games are selected, default back to "All"
        if (newSelection.length === 0) {
          newSelection = ['All']
        }
        
        return newSelection
      }
    })
  }, [])

  // Trigger search when filters change
  useEffect(() => {
    debouncedSearch(searchTerm)
  }, [searchMode, sortBy, showOfficialOnly, selectedGames, debouncedSearch, searchTerm])

  return (
    <div>
      <div className='search-container'>
        <div className='form'>
          <label htmlFor='search-input' className='sr-only'>
            Search The Rifter magazine archives
          </label>
          <input
            id='search-input'
            type='search'
            placeholder='Search The Rifter'
            onChange={handleChange}
            value={searchTerm}
            aria-describedby='search-help'
            autoComplete='off'
            spellCheck='false'
          />
          <button type='submit' aria-label='Search'>
            Search
          </button>
          <div id='search-help' className='sr-only'>
            Enter keywords to search through Rifter magazine articles, authors, artists, and content
          </div>
        </div>

        {/* Search Mode Selector */}
        <div className='search-controls'>
          <div className='search-mode-selector'>
            <label>Search Mode:</label>
            <select value={searchMode} onChange={(e) => handleSearchModeChange(e.target.value)}>
              <option value='all'>All Fields</option>
              <option value='articles'>Articles Only</option>
              <option value='authors'>Authors Only</option>
              <option value='issues'>Issues Only</option>
            </select>
          </div>

          {/* Sort Options */}
          <div className='sort-selector'>
            <label>Sort By:</label>
            <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
              <option value='relevance'>Relevance</option>
              <option value='author'>Author</option>
              <option value='issue'>Issue Title</option>
            </select>
          </div>
        </div>

        {/* Filters */}
        <div className='search-filters'>
          <div className='filter-group'>
            <label>
              <input
                type='checkbox'
                checked={showOfficialOnly}
                onChange={(e) => handleOfficialFilter(e.target.checked)}
              />
              Official Content Only
            </label>
          </div>

          <div className='filter-group'>
            <label>Game Systems:</label>
            <div className='game-filters'>
              <label key='All' className='game-filter-item all-filter'>
                <input
                  type='checkbox'
                  checked={selectedGames.includes('All')}
                  onChange={(e) => handleGameFilter('All', e.target.checked)}
                />
                All
              </label>
              {availableGames.map(game => (
                <label key={game} className='game-filter-item'>
                  <input
                    type='checkbox'
                    checked={selectedGames.includes(game)}
                    onChange={(e) => handleGameFilter(game, e.target.checked)}
                  />
                  {game}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {isLoading && searchTerm.trim() ? (
        <section aria-label='Loading search results'>
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </section>
      ) : searchResults.length > 0 ? (
        <section aria-label={`Search results for "${searchTerm}"`}>
          <div className='search-results-header'>
            <h2>Search Results</h2>
            <p className='results-count'>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
              {searchTerm && ` for "${searchTerm}"`}
            </p>
          </div>
          {searchResults.map(rifter => (
            <RifterCard
              key={rifter.id || rifter.issueTitle}
              rifter={rifter}
              searchTerm={searchTerm}
              highlightText={highlightText}
            />
          ))}
        </section>
      ) : searchTerm.trim() ? (
        <div className='search-results-text' role='status' aria-live='polite'>
          <p>
            No results found for "{searchTerm}". Try adjusting your search terms or filters.
          </p>
        </div>
      ) : null}
      
      <section aria-label='All Rifter issues'>
        <h2 className='sr-only'>Browse All Issues</h2>
        <div className='rifter-thumb-container' role='grid' aria-label='Grid of Rifter magazine covers'>
          {deDupArr.map(rifter => (
            <RifterThumb
              key={rifter.issueTitle || rifter.id}
              rifter={rifter}
              listArticles={listArticles}
              issueTitle={rifter.issueTitle}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

export default RifterList
